import React, { FormEvent, useCallback, useEffect, useState } from "react"
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import api from "../../api"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { RootState } from "../../store"
import { editPallet, resetSuccess } from "../../store/linehaul.store"
import ConsolAccordionBody from "../Consolidations/ConsolAccordionBody"
import ReadyBagsTable from "./ReadyBagsTable"

const PalletModal = ({
    show,
    setShow,
    warehouse,
    errors,
    setErrors,
    setSelectedRows,
    selectedRows,
    options,
    setOptions,
    showMessage
    // reloadAfterSubmit
}: {
    show: string
    setShow: Function
    warehouse: number
    errors: Record<string, any>
    setErrors: Function
    setSelectedRows: Function
    selectedRows: number[]
    options: Record<string, any>
    setOptions: Function
    showMessage: (message: string, variant: string, title: string) => void
    // reloadAfterSubmit:Function
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [boxWeightAndDims, setBoxWeightAndDims] = useState<Record<string, any>>({})
    const [selectedBoxes, setSelectedBoxes] = useState<string[]>([])
    const [remarks, setRemarks] = useState<string>("")
    //const [isDisabled, setIsDisabled] = useState(false)
    const [messageTextAreaLength, setMessageTextAreaLength] = useState(500 - remarks?.length || 500)
    const {
        country,
        editingPallet,
        bags_transit: { data: allBags, loading: loadingBags }
    } = useAppSelector((state: RootState) => state.linehaul)
    const [bagsEdit, setBagsEdit] = useState(allBags?.bags ?? [])
    const [dimsAndWeightUnit, setDimsAndWeightUnit] = useState({ weightUnit: "kg", dimsUnit: "cm" } as Record<
        string,
        any
    >)

    const [weightLimitExceeded, setWeightLimitExceeded] = useState(0)

    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState<boolean>(false)

    const dimsAndWeight: Record<string, any> = {
        height: {
            dimValue: boxWeightAndDims?.height,
            unitValue: dimsAndWeightUnit.dimsUnit,
            units: ["cm"]
        },
        width: {
            dimValue: boxWeightAndDims?.width,
            unitValue: dimsAndWeightUnit.dimsUnit,
            units: ["cm"]
        },
        length: {
            dimValue: boxWeightAndDims?.length,
            unitValue: dimsAndWeightUnit.dimsUnit,
            units: ["cm"]
        },
        weight: {
            dimValue: boxWeightAndDims?.weight,
            unitValue: dimsAndWeightUnit.weightUnit,
            units: ["kg"]
        },
        ch_weight: {
            dimValue: boxWeightAndDims?.ch_weight,
            unitValue: dimsAndWeightUnit.weightUnit,
            units: ["kg"]
        }
    }

    const checkRequiredFields = (data: Record<string, any>) => {
        let safe = true
        let fieldsWithIssues = {} as Record<string, any>
        Object.values(data).forEach((f, index) => {
            if (!f || f?.length === 0 || parseFloat(f) === 0) {
                safe = false
                if (Object.keys(data)[index]) {
                    fieldsWithIssues[`${Object.keys(data)[index]}`] =
                        "This field is required, and must be more than 0"
                }
            }
        })
        return {
            status: safe,
            issues: fieldsWithIssues
        }
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (show === "deletePallet" && editingPallet.id) {
            try {
                setSubmitting(true)
                const response = await api.linehaul.deletePallet({
                    palletId: editingPallet.id
                })
                if (!response?.data?.status)
                    throw new Error(response.data?.message ?? "Unable to delete pallet")
                setSubmitting(false)
                showMessage(response.data?.message ?? "Pallet deleted successfully", "success", "Success")
                handleClose()
                dispatch(resetSuccess(true))
            } catch (e: any) {
                setSubmitting(false)
                showMessage(e.message ?? "Unable to update pallet", "danger", "Error")
                handleClose()
                dispatch(resetSuccess(true))
            }
            return
        }
        const { ch_weight, height, length, weight, width } = boxWeightAndDims
        const { dimsUnit, weightUnit } = dimsAndWeightUnit
        let validity = checkRequiredFields({ height, length, weight, width, dimsUnit, weightUnit })
        if (validity?.status) {
            // if (
            //   !height ||
            //   !width ||
            //   !length ||
            //   !ch_weight ||
            //   // !selectedBoxes.length ||
            //   !dimsUnit ||
            //   !weightUnit
            // )
            //   return
            setSubmitting(true)
            if (show === "updatePallet" && editingPallet) {
                const palletData = {
                    warehouse,
                    destination: country,
                    height,
                    length,
                    width,
                    dimensions_unit: dimsUnit,
                    weight_unit: weightUnit,
                    actual_weight_value: weight,
                    ch_weight_value: ch_weight,
                    remarks,
                    bags: selectedBoxes,
                    id: editingPallet.id
                }
                try {
                    setSubmitting(true)
                    const response = await api.linehaul.updatePallet({ palletData })
                    if (!response?.data?.status)
                        throw new Error(response.data?.message ?? "Unable to update pallet")
                    setSubmitting(false)
                    showMessage(response.data?.message ?? "Pallet updated successfully", "success", "Success")
                    handleClose()
                    const lhURL = response.data.payload.lh_pallet_url ?? ""
                    if (lhURL) window.open(lhURL, "_blank", "noreferrer")
                    dispatch(resetSuccess(true))
                    // reloadAfterSubmit()
                } catch (e: any) {
                    setSubmitting(false)
                    showMessage(e.message ?? "Unable to update pallet", "danger", "Error")
                    handleClose()
                    dispatch(resetSuccess(undefined))
                }
            } else {
                const palletData = {
                    warehouse,
                    destination: country,
                    height,
                    length,
                    width,
                    dimensions_unit: dimsUnit,
                    weight_unit: weightUnit,
                    actual_weight_value: weight,
                    ch_weight_value: ch_weight,
                    remarks,
                    bags: selectedBoxes
                }
                try {
                    const response = await api.linehaul.createPallet({ palletData })
                    if (!response?.data?.status)
                        throw new Error(response.data?.message ?? "Unable to create a pallet")
                    setSubmitting(false)
                    showMessage(response?.data?.message ?? "Pallet created successfully", "success", "Success")
                    handleClose()
                    const lhURL = response.data.payload.lh_pallet_url ?? ""
                    if (lhURL) window.open(lhURL, "_blank", "noreferrer")
                    dispatch(resetSuccess(true))
                    // reloadAfterSubmit()
                } catch (e: any) {
                    setSubmitting(false)
                    showMessage(e.message ?? "Unable to create a pallet", "danger", "Error")
                    handleClose()
                    dispatch(resetSuccess(undefined))
                }
            }
        } else {
            setErrors({
                ...(validity?.issues || {})
            })
        }
    }

    const handleClose = useCallback(() => {
        setShow("")
        setSelectedRows([])
        dispatch(editPallet(undefined))
        setErrors({})
    }, [dispatch, setShow])

    useEffect(() => {
        const handleAsyncLogic = async () => {
            if (show === "updatePallet" && editingPallet) {
                setLoading(true)
                try {
                    const response = await api.linehaul.palletDetails({
                        palletId: editingPallet.pallet_id
                    })
                    if (!response?.data?.status || !response.data.payload)
                        throw new Error(response?.data?.message ?? "Unable to get pallet data")
                    const {
                        LHBags,
                        height,
                        width,
                        length,
                        dimensions_unit,
                        actual_weight_value,
                        ch_weight_value,
                        weight_unit,
                        remarks
                    } = response?.data?.payload
                    setSelectedBoxes((LHBags ?? []).map((bag: any) => bag.id))
                    setBagsEdit(
                        (allBags.bags?.filter((bag: any) => !!bag.parcels_count) ?? []).concat(LHBags ?? [])
                    )
                    setBoxWeightAndDims({
                        ch_weight: ch_weight_value ?? 0,
                        height: height ?? 0,
                        length: length ?? 0,
                        weight: actual_weight_value ?? 0,
                        width: width ?? 0
                    })
                    setDimsAndWeightUnit({
                        weightUnit: weight_unit ?? "",
                        dimsUnit: dimensions_unit ?? ""
                    })
                    setRemarks(remarks ?? "")
                    setLoading(false)
                } catch (e: any) {
                    setLoading(false)
                    showMessage(e.message ?? "Unable to get pallet data", "danger", "Error")
                    dispatch(resetSuccess(undefined))
                    handleClose()
                }
            }
        }
        handleAsyncLogic()
    }, [])

    return (
        <Modal
            size={show === "deletePallet" ? "lg" : "xl"}
            show={["createPallet", "updatePallet", "deletePallet"].includes(show)}
            onHide={handleClose}
            onClose={handleClose}
            backdrop="static"
            scrollable
            keyboard={false}
            dialogClassName={show === "deletePallet" ? "" : "xxl-modal"}>
            <Modal.Header closeButton={!submitting}>
                <Modal.Title className="text-uppercase">
                    {show === "updatePallet"
                        ? t("Update Pallet")
                        : show === "deletePallet"
                          ? `${t("Delete")} ${editingPallet.pallet_id}`
                          : t("Create Pallet")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="modal-gray-bg">
                {loading || loadingBags ? (
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only"></span>
                        </div>
                    </div>
                ) : (
                    <Row>
                        <Col>
                            {show !== "deletePallet" ? (
                                <div className="main-container modal-gray-bg">
                                    <ReadyBagsTable
                                        data={
                                            editingPallet && show === "updatePallet"
                                                ? bagsEdit
                                                : (allBags.bags?.filter((bag: any) => !!bag.parcels_count) ??
                                                  [])
                                        }
                                        setSelectedRows={setSelectedBoxes}
                                        selectedRows={selectedBoxes}
                                    />
                                </div>
                            ) : null}
                            <Form onSubmit={handleSubmit} id="pallet-form" className="font-13">
                                {show === "deletePallet" ? (
                                    <Row className="px-1 align-items-start">
                                        <Col>
                                            {(editingPallet?.LHBags?.length ?? 0) <= 0 ? (
                                                <span>Are you sure you want to delete</span>
                                            ) : (
                                                <span className="text-danger">
                                                    Unable to delete a non empty pallet
                                                </span>
                                            )}
                                        </Col>
                                    </Row>
                                ) : (
                                    <>
                                        <Row className="px-1 align-items-start">
                                            {Object.keys(dimsAndWeight)?.map((key: string) => (
                                                <Col key={key} xs={12} md={6} lg>
                                                    <ConsolAccordionBody
                                                        anyStep
                                                        dimValue={dimsAndWeight[key].dimValue}
                                                        dimFieldName={key}
                                                        unitFieldName={
                                                            key === "weight" ? "weightUnit" : "dimsUnit"
                                                        }
                                                        unitValue={dimsAndWeight[key].unitValue}
                                                        dimsAndWeightUnit={dimsAndWeightUnit}
                                                        setDimsAndWeightUnit={setDimsAndWeightUnit}
                                                        boxWeightAndDims={boxWeightAndDims}
                                                        setBoxWeightAndDims={setBoxWeightAndDims}
                                                        units={dimsAndWeight[key].units}
                                                        setWeightLimitExceeded={setWeightLimitExceeded}
                                                        setSelectedBoxes={setSelectedBoxes}
                                                        boxes={selectedBoxes}
                                                        errors={errors}
                                                        setErrors={setErrors}
                                                        warehouse={warehouse}
                                                        pallet={true}
                                                    />
                                                </Col>
                                            ))}
                                        </Row>
                                        <Row className="px-1 justify-content-center align-items-center">
                                            <Col>
                                                <Form.Group className="mb-3 w-100">
                                                    <Form.Label htmlFor="remarks">{t("Remarks")}</Form.Label>
                                                    <Form.Control
                                                        value={remarks}
                                                        maxLength={500}
                                                        as="textarea"
                                                        name={"remarks"}
                                                        autoComplete="off"
                                                        onChange={(e: any) => {
                                                            let textAreaValue = e.target.value
                                                            setMessageTextAreaLength(
                                                                500 - textAreaValue?.length
                                                            )
                                                            setRemarks(textAreaValue)
                                                        }}
                                                        placeholder="Remarks..."
                                                    />
                                                    <div className="float-end">
                                                        <p
                                                            className={`mt-2 ${messageTextAreaLength <= 0 && "text-danger"}`}>
                                                            {messageTextAreaLength} {t("Chars left")}
                                                        </p>
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </Form>
                        </Col>
                    </Row>
                )}
                <Row className="px-1 mt-3">
                    <Col className="d-flex align-items-center justify-content-end">
                        <Button
                            type="button"
                            disabled={submitting || loading}
                            className="float-end mb-2 gray-bg px-3 mx2"
                            onClick={handleClose}>
                            {t("Cancel")}
                        </Button>
                        <Button
                            disabled={
                                submitting ||
                                loading ||
                                (show === "deletePallet" && (editingPallet?.LHBags?.length ?? 0) > 0)
                            }
                            type="submit"
                            form="pallet-form"
                            className="float-end mb-2 primary-bg px-3 mx-2">
                            {submitting || loading ? (
                                <div className="text-center">
                                    <div className="spinner-border spinner-border-sm" role="status">
                                        <span className="sr-only"></span>
                                    </div>
                                </div>
                            ) : show === "deletePallet" ? (
                                t("Delete")
                            ) : (
                                t("Save")
                            )}
                        </Button>
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    )
}

export default PalletModal
